import { useEffect, useRef } from "react";

/*
 * The room's keys, on one capture-phase listener on window while the room is open:
 *   j / ↓  next item          k / ↑  previous item
 *   space  play / pause
 *   Enter  enlarge the open capture (a focused button keeps its own Enter, except a queue item's head)
 *   Esc    close the lightbox, else close the room
 *   ← / →  step the lightbox's captures while it is open
 * Nothing is taken from a text field, a select, an editable element, an antd
 * select, or while an antd modal or drawer is open; a key with Ctrl, Alt or
 * Cmd is left to the browser. A key the room handles does not reach the page behind.
 */

const TYPING_TAGS = ["INPUT", "TEXTAREA", "SELECT"];

/** The key was pressed where the room must not take it. */
export function isForeignTarget(target) {
  const el = target && target.nodeType === 1 ? target : null;
  if (!el) return false;
  if (TYPING_TAGS.includes(el.tagName)) return true;
  if (el.isContentEditable || (el.getAttribute && el.getAttribute("contenteditable") === "true")) return true;
  if (el.closest && el.closest(".ant-select, .ant-select-dropdown, .ant-picker, [contenteditable='true']")) return true;
  return false;
}

/** An antd modal or drawer is open on the page (the room's own lightbox is not one). */
export function isOverlayOpen(doc = document) {
  const wraps = Array.from(doc.querySelectorAll(".ant-modal-wrap"));
  if (wraps.some((w) => w.style.display !== "none")) return true;
  return !!doc.querySelector(".ant-drawer-open");
}

/**
 * @param {boolean} active  the room is open
 * @param {Object} handlers read afresh on every key
 * @param {boolean} handlers.zoomOpen
 * @param {Function} handlers.next
 * @param {Function} handlers.prev
 * @param {Function} handlers.toggle
 * @param {Function} handlers.zoom
 * @param {Function} handlers.zoomStep  (±1)
 * @param {Function} handlers.closeZoom
 * @param {Function} handlers.close
 */
export default function useReviewKeys(active, handlers) {
  const ref = useRef(handlers);
  ref.current = handlers;

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isForeignTarget(e.target) || isOverlayOpen()) return;
      const h = ref.current || {};
      const take = (fn, ...args) => {
        if (typeof fn !== "function") return;
        e.preventDefault();
        e.stopPropagation();
        fn(...args);
      };
      if (h.zoomOpen) {
        if (e.key === "Escape") take(h.closeZoom);
        else if (e.key === "ArrowRight") take(h.zoomStep, 1);
        else if (e.key === "ArrowLeft") take(h.zoomStep, -1);
        return;
      }
      switch (e.key) {
        case "Escape": take(h.close); break;
        case "j": case "ArrowDown": take(h.next); break;
        case "k": case "ArrowUp": take(h.prev); break;
        case " ": case "Spacebar": take(h.toggle); break;
        case "Enter": {
          /* Enter on a focused button or link keeps its own meaning, except on a queue item's head
             (marked data-room-enter), where it enlarges the open capture */
          const t = e.target && e.target.closest ? e.target : null;
          const onControl = t && t.closest("button, a, [role='button']") && !t.closest("[data-room-enter]");
          if (!onControl) take(h.zoom);
          break;
        }
        default: break;
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [active]);
}
